import './RangeSelector.css';
import React, { useState } from 'react';

interface RangeSelectorProps {
  value: [number, number];
  onChange: (nums: [number, number]) => void;
  warningCondition?: (vals: [number, number]) => string | undefined;
  options: {
    value: number;
    label: string;
  }[];
}

export default function RangeSelector({
  options,
  value,
  onChange,
  warningCondition,
}: RangeSelectorProps) {

  function onSlideFn(aValue: number, dragInfo: DragInfo) {
    let [lo, hi] = dragInfo.value;
    if (dragInfo.mode === 'end') {
      if (aValue > dragInfo.start) {
        hi = aValue;
        lo = dragInfo.start;
      } else {
        lo = aValue;
        hi = dragInfo.start;
      }
      setDragInfo({ start: dragInfo.start, value: [lo, hi], mode: 'end' });
    } else {
      const [startLo, start, startHi] = dragInfo.start;
      const diff = aValue - start;
      const newLo = startLo + diff;
      const newHi = startHi + diff;
      if (newLo >= options[0].value && newHi <= options[options.length - 1].value) {
        setDragInfo({ start: dragInfo.start, value: [newLo, newHi], mode: 'between' });
      }
    }
  }

  function grabStart(ev: any, start: number) {
    const [lo, hi] = value;
    ev.preventDefault?.();
    if (start > lo && start < hi) {
      setDragInfo({ start: [lo, start, hi], value: [lo, hi], mode: 'between' });
    } else if (start > Math.min(lo, hi)) {
      setDragInfo({ start: lo, value: [lo, start], mode: 'end' });
    } else if (start < Math.max(lo, hi)) {
      setDragInfo({ start: hi, value: [start, hi], mode: 'end' });
    } else {
      setDragInfo({ start, value: [start, start], mode: 'end' });
    }
  }

  function grabEnd() {
    if (dragInfo) {
      onChange(dragInfo.value);
      setDragInfo(undefined);
    }
  }

  function grabMove(idx: number) {
    if (dragInfo) {
      onSlideFn(idx, dragInfo);
    }
  }

  type DragInfo = {
    start: number;
    value: [number, number];
    mode: 'end';
  } | {
    start: [number,number,number];
    value: [number, number];
    mode: 'between';
  }

  const [dragInfo, setDragInfo] = useState<DragInfo | undefined>();
  const warning = warningCondition?.(value);

  return (
    <div className="range-selector" onPointerLeave={grabEnd} onPointerUp={grabEnd}>
      {warning &&
        <div className="explain warning">
          <p><strong>Warning!</strong> { warning }</p>
        </div>
      }
      { options.map(({ label, value: aValue }) => {
        let [lo, hi] = dragInfo?.value ?? value;
        const classNames: string[] = [];
        if (aValue < lo || aValue > hi) classNames.push('excluded');
        if (aValue > lo && aValue < hi) classNames.push('between');
        if (aValue >= lo && aValue <= hi) classNames.push('included');
        if (aValue === lo || aValue === hi) classNames.push('control-point');
        if (aValue === options[0].value) classNames.push('begin-cap');
        if (aValue === options[options.length - 1].value) classNames.push('end-cap');
        if (dragInfo) classNames.push('dragging');

        const checked = lo <= aValue && aValue <= hi;

        return (
          <div key={label} className="range-selector-option" onPointerMove={() => grabMove(aValue)}>
            <span
              className={`slider-container ${classNames.join(' ')}`}
              onPointerDown={ev => grabStart(ev, aValue)}
            />
            <span
              className={`range-label ${checked ? 'selected' : 'unselected'}`}
              children={label}
            />
            <button
              className={`range-min-max range-max ${checked ? 'selected' : 'unselected'}`}
              onClick={() => onChange([Math.min(aValue, lo),aValue])}
              children={<MaxGlyph title={`set ${aValue} as maximum`}/>}
            />
            <button
              className={`range-min-max range-min ${checked ? 'selected' : 'unselected'}`}
              onClick={() => onChange([aValue,Math.max(aValue, hi)])}
              children={<MinGlyph title={`set ${aValue} as minimum`}/>}
            />
          </div>
        );
      }) }
    </div>
  );
}

const MaxGlyph = ({ title }: { title: string }) => <Glyph title={title} rotate={180}/>;
const MinGlyph = ({ title }: { title: string }) => <Glyph title={title} rotate={0}/>;

interface GlyphProps {
  rotate: number;
  title: string;
}

function Glyph({ rotate, title }: GlyphProps) {
  return (
    <svg viewBox="0 0 1000 1000">
      <title>{ title }</title>
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="30"
        strokeLinejoin="round"
        // transform={`rotate(${rotate} 500 500)`}
        transform={`translate(200,200) scale(0.6,0.6) rotate(${rotate} 500 500)`}
        d={[
          'm 0 0',
          'h 1000',
          'v 400',
          'h -125',
          'v -275',
          'h -750',
          'v 275',
          'h -125',
          'z',
          'M 425 250',
          'h 150',
          'v 150',
          'h -150',
          'z',
          'M 425 500',
          'h 150',
          'v 150',
          'h -150',
          'z',
          'M 425 750',
          'h 150',
          'v 150',
          'h -150',
          'z',
        ].join(' ')}
      />
    </svg>
  );
}
